import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { saveAffiliateRef, getAffiliateRef } from '@/lib/affiliateRef';

interface AffiliateData {
  affiliate_id: string | null;
  affiliate_code: string | null;
}

export const useAffiliateTracking = () => {
  const [affiliateData, setAffiliateData] = useState<AffiliateData>({
    affiliate_id: null,
    affiliate_code: null,
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const affiliateCode = urlParams.get('aff') || urlParams.get('affiliate');
    
    if (affiliateCode) {
      handleAffiliateCode(affiliateCode);
      saveAffiliateRef(affiliateCode); // remembered for 30 days, auto-applied at checkout
    } else {
      const storedCode = getAffiliateRef();
      if (storedCode) {
        setAffiliateData(prev => ({ ...prev, affiliate_code: storedCode }));
      }
    }
  }, [location.search]);

  const handleAffiliateCode = async (code: string) => {
    try {
      // 1. Check affiliate_codes table first (user-generated codes)
      const { data: affiliateCode } = await supabase
        .from('affiliate_codes')
        .select('id, code, event_id, description')
        .eq('code', code.toUpperCase())
        .eq('is_active', true)
        .maybeSingle();

      if (affiliateCode) {
        // Track the click
        await supabase.from('affiliate_code_clicks').insert({
          affiliate_code_id: affiliateCode.id,
          visitor_id: generateVisitorId(),
          user_agent: navigator.userAgent,
          referrer_url: document.referrer,
        });

        // Redirect to event if event_id is set
        let eventId = affiliateCode.event_id;
        
        // Fallback: parse event_id from description for legacy codes
        if (!eventId && affiliateCode.description) {
          const match = affiliateCode.description.match(/\(([0-9a-f-]{36})\)/);
          if (match) eventId = match[1];
        }

        if (eventId) {
          await redirectToEvent(eventId);
        }
        return;
      }

      // 2. Fallback: Check affiliate_partners table (legacy partners)
      // affiliate_partners isn't readable by visitors (RLS) — use the public lookup
      const { data: affRows } = await (supabase as any)
        .rpc('lookup_affiliate_partner_code', { p_code: code.toUpperCase() });
      const affiliate: { id: string; tracking_url: string | null } | null = Array.isArray(affRows) ? affRows[0] : null;

      if (affiliate) {
        setAffiliateData({
          affiliate_id: affiliate.id,
          affiliate_code: code,
        });

        // Attribute the click to the event the visitor landed on (so per-event
        // "visits via link" actually counts). Resolve /events/<cat>/<slug|id>.
        let landedEventId: string | null = null;
        const m = window.location.pathname.match(/\/events\/[^/]+\/([^/?#]+)/);
        if (m) {
          const seg = decodeURIComponent(m[1]);
          const bySlug = await supabase.from('festivals').select('id').eq('slug', seg).maybeSingle();
          landedEventId = (bySlug.data as any)?.id ?? null;
          if (!landedEventId && /^[0-9a-f-]{36}$/i.test(seg)) {
            const byId = await supabase.from('festivals').select('id').eq('id', seg).maybeSingle();
            landedEventId = (byId.data as any)?.id ?? null;
          }
        }

        // Fallback (e.g. old homepage links without an event in the URL): use
        // the affiliate's linked event so those clicks still attribute — no need
        // for the affiliate to re-share a new link.
        const { data: campaign } = await supabase
          .from('affiliate_campaigns')
          .select('event_id')
          .eq('affiliate_id', affiliate.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const attributedEventId = landedEventId ?? (campaign?.event_id ?? null);

        // Track the click
        const click = {
          affiliate_id: affiliate.id,
          event_id: attributedEventId,
          visitor_id: generateVisitorId(),
          ip_address: await getClientIP(),
          user_agent: navigator.userAgent,
          referrer_url: document.referrer,
          click_timestamp: new Date().toISOString(),
        };
        const { error: clickErr } = await supabase.from('affiliate_tracking').insert(click);
        // affiliate_tracking.event_id references retreats only — for festival
        // pages the insert is rejected, so record the click without the event.
        if (clickErr && attributedEventId) {
          await supabase.from('affiliate_tracking').insert({ ...click, event_id: null });
        }

        // If they landed on the homepage but the affiliate is tied to an event,
        // send them there.
        if (!landedEventId && campaign?.event_id) {
          await redirectToEvent(campaign.event_id);
          return;
        }

        // Fallback: check tracking_url on the partner
        const partnerData = affiliate;

        if (partnerData?.tracking_url) {
          const url = partnerData.tracking_url;
          if (url.startsWith('/')) {
            navigate(url, { replace: true });
          } else if (url.startsWith('http')) {
            window.location.href = url;
          }
        }
      }
    } catch (error) {
      console.error('Error tracking affiliate click:', error);
    }
  };

  const redirectToEvent = async (eventId: string) => {
    const eventTables: { table: string; urlCategory: string }[] = [
      { table: 'festivals', urlCategory: 'festivals' },
      { table: 'retreats', urlCategory: 'retreats' },
      { table: 'academies', urlCategory: 'academies' },
      { table: 'workations', urlCategory: 'workations' },
    ];

    for (const { table, urlCategory } of eventTables) {
      const { data: event } = await supabase
        .from(table as any)
        .select('slug, id')
        .eq('id', eventId)
        .maybeSingle();

      if (event) {
        const slug = (event as any).slug || eventId;
        navigate(`/events/${urlCategory}/${slug}`, { replace: true });
        return;
      }
    }
  };

  const trackConversion = async (orderId: string, commissionAmount: number, productId?: string) => {
    if (affiliateData.affiliate_id) {
      try {
        let finalCommissionAmount = commissionAmount;
        
        if (productId) {
          const { data: product } = await supabase
            .from('products')
            .select('affiliate_commission_enabled, affiliate_commission_type, affiliate_commission_value, price')
            .eq('id', productId)
            .single();

          if (product?.affiliate_commission_enabled) {
            if (product.affiliate_commission_type === 'percentage' && product.affiliate_commission_value) {
              finalCommissionAmount = (product.price * product.affiliate_commission_value) / 100;
            } else if (product.affiliate_commission_type === 'fixed' && product.affiliate_commission_value) {
              finalCommissionAmount = product.affiliate_commission_value;
            }
          }
        }

        await supabase
          .from('affiliate_tracking')
          .update({
            order_id: orderId,
            commission_amount: finalCommissionAmount,
            conversion_timestamp: new Date().toISOString(),
          })
          .eq('affiliate_id', affiliateData.affiliate_id)
          .eq('visitor_id', generateVisitorId())
          .is('conversion_timestamp', null);

        await checkAffiliatePerformance(affiliateData.affiliate_id);
      } catch (error) {
        console.error('Error tracking conversion:', error);
      }
    }
  };

  const checkAffiliatePerformance = async (affiliateId: string) => {
    try {
      const { data: affiliate } = await supabase
        .from('affiliate_partners')
        .select('*')
        .eq('id', affiliateId)
        .single();

      if (!affiliate) return;

      const { data: trackingData } = await supabase
        .from('affiliate_tracking')
        .select('*')
        .eq('affiliate_id', affiliateId);

      if (!trackingData) return;

      const totalClicks = trackingData.length;
      const conversions = trackingData.filter(t => t.conversion_timestamp);
      const totalConversions = conversions.length;
      const totalCommission = conversions.reduce((sum, t) => sum + (t.commission_amount || 0), 0);
      const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;

      const milestones = [100, 500, 1000, 5000, 10000];
      const highConversionThreshold = 10;

      const reachedMilestone = milestones.find(m => 
        totalCommission >= m && totalCommission < m + (affiliate.commission_rate || 0)
      );

      if (reachedMilestone) {
        await supabase.functions.invoke('send-affiliate-notification', {
          body: {
            affiliateName: affiliate.name,
            affiliateEmail: affiliate.email,
            affiliateCode: affiliate.affiliate_code,
            notificationType: 'milestone',
            totalCommission,
            totalConversions,
            milestone: reachedMilestone,
          },
        });
      }

      if (conversionRate >= highConversionThreshold && totalConversions >= 10) {
        await supabase.functions.invoke('send-affiliate-notification', {
          body: {
            affiliateName: affiliate.name,
            affiliateEmail: affiliate.email,
            affiliateCode: affiliate.affiliate_code,
            notificationType: 'high_conversion',
            conversionRate,
            totalCommission,
            totalConversions,
          },
        });
      }
    } catch (error) {
      console.error('Error checking affiliate performance:', error);
    }
  };

  const generateVisitorId = () => {
    let visitorId = localStorage.getItem('visitor_id');
    if (!visitorId) {
      visitorId = 'visitor_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      localStorage.setItem('visitor_id', visitorId);
    }
    return visitorId;
  };

  const getClientIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return null;
    }
  };

  return {
    affiliateData,
    trackConversion,
  };
};
